import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import { Avatar } from 'shared/components/ui/Avatar/Avatar';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { coinIcons, ROUTES } from 'shared/constants';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';
import { useQuestsStore } from 'shared/store/questsStore';
import { genericFormatBet } from 'shared/utils/betFormater';
import { prettyTx } from 'shared/utils/pretty-tx';
import { CurrencyType } from 'shared/web3';

import Heart from 'assets/png/heart-filled.png?format=webp;avif&as=picture';
import ControllerIcon from 'assets/svg/controller.svg?react';
import DoorIcon from 'assets/svg/door.svg?react';
import EnvelopIcon from 'assets/svg/envelop.svg?react';
import Loader from 'assets/svg/loader.svg?react';

import { GameState, GameStateConfig } from './live-game-item-mobile.types';
import useLiveGameItem from './use-live-game-item';

interface LiveGameItemProps {
  roomId: BigInt;
  address: string;
  currency?: CurrencyType;
  amount: number;
  ourGame?: boolean;
  joinRoomHandler?: (roomId: number, bet?: number) => void;
  status: RoomStatusEnum;
  playerB?: `0x${string}`;
  wins?: number;
}

export const LiveGameItemMobile = ({
  roomId,
  address,
  currency,
  amount,
  ourGame,
  joinRoomHandler,
  status,
  playerB,
  wins,
}: LiveGameItemProps) => {
  const navigate = useNavigate();
  const { address: userAddress, chainId } = useAccount();
  const { setSnapshotPoints } = useQuestsStore();
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const { t } = useTranslation();

  const getGameState = (): GameState => {
    if (
      ourGame &&
      status === RoomStatusEnum.Open &&
      playerB !== NULL_ADDRESS &&
      userAddress?.toLowerCase() === playerB?.toLowerCase()
    )
      return GameState.PendingInvite;
    if (ourGame) return GameState.OurGame;
    return GameState.OtherGame;
  };

  const getGameStateConfig = (gameState: GameState): GameStateConfig => {
    const configs: Record<GameState, GameStateConfig> = {
      [GameState.PendingInvite]: {
        textColor: 'text-light-green',
        buttonText: t('common.acceptInvite'),
        gradientClasses: 'from-[#0D1450] to-[#151552]',
        icon: <EnvelopIcon />,
      },
      [GameState.OurGame]: {
        textColor: 'text-[#99D4FF]',
        buttonText: t('common.return'),
        gradientClasses: 'from-[#0D1450] to-[#151552]',
        icon: <DoorIcon />,
      },
      [GameState.OtherGame]: {
        textColor: 'text-orange',
        buttonText: t('common.play'),
        gradientClasses: 'from-[#0D1450] to-[#151552]',
        icon: <ControllerIcon />,
      },
    };
    return configs[gameState];
  };

  const handleButtonClick = () => {
    switch (gameState) {
      case GameState.OurGame:
        setSnapshotPoints();
        navigate(ROUTES.ROOM + `/${chainId}/${Number(roomId)}`);
        break;
      case GameState.PendingInvite:
      case GameState.OtherGame:
      default:
        setSnapshotPoints();
        handleJoinGame();
        break;
    }
  };

  const gameState = getGameState();
  const gameConfig = getGameStateConfig(gameState);

  const { isError, errorMessage, loading, handleJoinGame, normalizedAmount } = useLiveGameItem({
    currency,
    userAddress,
    roomId: Number(roomId),
    amount,
    containerRef: null,
    joinRoomHandler,
  });

  const screenAddress = useMemo(
    () => prettyTx({ address, leaderboardMap, startAddressLength: 2, discordUsernameLength: 9 }),
    [address, leaderboardMap],
  );
  return (
    <div className="w-full" data-testid="live-game-item">
      <div
        className={clsx(
          'grid grid-cols-[2fr_1fr_1fr_1.5fr] rounded-lg border border-[rgba(255,255,255,0.25)] bg-gradient-to-r p-0.5 pr-2 transition-all sm:rounded-xl sm:border-2',
          gameConfig.gradientClasses,
        )}
      >
        <div className="items flex items-center gap-1">
          <div className="h-7 w-7 rounded-[4px] bg-[linear-gradient(180deg,#000BFF_0%,#FFC000_100%)] p-[1px] sm:rounded-xl">
            <Avatar userAddress={`0x${address}`} className="rounded-[4px] sm:rounded-xl" />
          </div>
          <div className="flex flex-col">
            <span className="font-chivo text-[8px] font-medium leading-3 text-yellow">
              {screenAddress}
            </span>
            <span className="text-[8px] font-medium leading-[10px] text-white opacity-75">
              {t('pages.liveGamesSection.id')} {roomId.toString()}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-1">
          {currency?.symbol ? (
            <img
              src={coinIcons[currency.symbol as keyof typeof coinIcons]}
              alt="coin"
              className="h-[18px] w-[18px]"
            />
          ) : null}
          <div>
            <p className="text-[10px] font-semibold leading-[10px] text-white">
              {genericFormatBet(
                normalizedAmount.toString(),
                'text-[10px] sm:text-[12px] mb-2 px-0.5',
              )}
            </p>

            <p className="text-[10px] font-semibold text-white opacity-75">{currency?.symbol}</p>
          </div>
        </div>
        <div className="flex items-center justify-center">
          <p className="mr-0.5 text-md font-semibold text-white">{wins}</p>
          <OptimizedImage imageData={Heart} alt="heart" className="h-4 w-4" />
        </div>
        <div className="flex items-center justify-end">
          <button
            className={clsx(
              'flex min-w-[60px] items-center justify-end gap-0.5 text-sm font-extrabold uppercase',
              gameConfig.textColor,
            )}
            onClick={handleButtonClick}
          >
            {' '}
            {loading ? (
              <Loader className="h-4 w-4 animate-spin fill-[#fff] text-[#FFC000] sm:h-8 sm:w-8" />
            ) : isError ? (
              errorMessage
            ) : (
              <>
                {gameConfig.icon}
                {gameConfig.buttonText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
